import { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function RecordDialog({ open, title, columns, initial, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial || {});
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="record-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-lg bg-background shadow-xl border max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="record-dialog-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 overflow-auto attg-scroll grid grid-cols-1 sm:grid-cols-2 gap-4">
          {columns.map((col) => (
            <div key={col.key} className={col.type === "text" && col.key === "nombre" ? "sm:col-span-2" : ""}>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{col.label}</label>
              {col.type === "select" ? (
                <select
                  value={form[col.key] ?? ""}
                  onChange={(e) => set(col.key, e.target.value)}
                  data-testid={`field-${col.key}`}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                >
                  <option value="">—</option>
                  {col.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                  step="any"
                  value={form[col.key] ?? ""}
                  onChange={(e) => set(col.key, e.target.value)}
                  data-testid={`field-${col.key}`}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="inline-flex items-center rounded-md border border-border bg-card h-9 px-4 text-sm font-medium hover:bg-slate-50">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="record-dialog-save"
            className="inline-flex items-center rounded-md bg-primary text-primary-foreground h-9 px-4 text-sm font-medium shadow-sm hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
